import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, User, Mail, ShieldCheck, Github, Twitter, X, Hash } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import ImageDropzone from '../components/ImageDropzone';

const AdminTeamPage = () => {
  const [team, setTeam] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    about_text: '',
    image: '',
    display_order: 0
  });

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await axios.get(`${apiUrl}/api/team`);
      setTeam(res.data || []);
    } catch (err) {
      toast.error('Failed to sync team database');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      if (editingMember) {
        await axios.put(`${apiUrl}/api/team/${editingMember.id}`, formData);
        toast.success('Personnel record updated');
      } else {
        await axios.post(`${apiUrl}/api/team`, formData);
        toast.success('New team member registered');
      }
      setShowModal(false);
      setEditingMember(null);
      fetchTeam();
    } catch (err) {
      toast.error('Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('De-register this member from the core database?')) {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        await axios.delete(`${apiUrl}/api/team/${id}`);
        toast.success('Record purged');
        fetchTeam();
      } catch (err) {
        toast.error('Purge failed');
      }
    }
  };

  const openEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      role: member.role,
      about_text: member.about_text,
      image: member.image || '',
      display_order: member.display_order
    });
    setShowModal(true);
  };

  return (
    <div className="space-y-10 animate-fade-in pb-12">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-white/5">
        <div>
          <h1 className="admin-heading text-4xl font-black text-white tracking-tight">Personnel Index</h1>
          <p className="text-slate-500 font-medium mt-1">Direct management of the Quivix core architect network.</p>
        </div>
        <button 
          onClick={() => { setEditingMember(null); setFormData({ name: '', role: '', about_text: '', image: '', display_order: 0 }); setShowModal(true); }}
          className="btn-premium btn-premium-primary shadow-indigo group"
        >
          <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
          <span>Onboard Architect</span>
        </button>
      </div>

      {/* Team Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <AnimatePresence mode="popLayout">
          {team.map((member, idx) => (
            <motion.div 
              key={member.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.05 }}
              className="glass-card group flex flex-col md:flex-row items-center md:items-stretch gap-8 p-8 relative overflow-hidden border-white/5 hover:border-indigo-500/20 transition-all duration-500"
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500/0 group-hover:bg-indigo-500/40 transition-all" />
              
              {/* Profile Image Container */}
              <div className="relative shrink-0">
                <div className="w-32 h-32 rounded-3xl bg-slate-900 border-2 border-white/5 overflow-hidden group-hover:border-indigo-500/30 transition-all duration-500 transform group-hover:scale-105 shadow-2xl">
                  {member.image ? (
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-700 bg-slate-900">
                      <User size={48} />
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center text-white shadow-lg border border-[#030712] transform group-hover:rotate-12 transition-transform">
                   <ShieldCheck size={20} />
                </div>
              </div>

              {/* Info Container */}
              <div className="flex-1 text-center md:text-left flex flex-col">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="admin-heading text-2xl font-black text-white italic group-hover:text-indigo-400 transition-colors uppercase tracking-widest">{member.name}</h3>
                    <p className="text-indigo-500 font-black text-[10px] uppercase tracking-[0.2em] mt-1 flex items-center gap-2 justify-center md:justify-start">
                      <Hash size={10} /> 00{member.display_order} / {member.role}
                    </p>
                  </div>
                  <div className="hidden md:flex gap-2">
                    <button onClick={() => openEdit(member)} className="p-2.5 rounded-xl bg-white/5 text-slate-500 hover:bg-white/10 hover:text-white transition-all border border-transparent hover:border-white/10">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleDelete(member.id)} className="p-2.5 rounded-xl bg-white/5 text-slate-500 hover:bg-red-500/10 hover:text-red-400 transition-all border border-transparent hover:border-red-500/20">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                <p className="text-slate-400 text-sm font-medium line-clamp-3 leading-relaxed mt-2 border-l-2 border-white/5 pl-4 md:pl-6 bg-white/[0.01] py-2">
                  {member.about_text}
                </p>

                <div className="mt-auto pt-6 flex items-center gap-4 justify-center md:justify-start grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-indigo-500/20 hover:text-indigo-400 cursor-pointer">
                    <Mail size={16} />
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-blue-500/20 hover:text-blue-400 cursor-pointer">
                    <Twitter size={16} />
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-white/20 hover:text-white cursor-pointer">
                    <Github size={16} />
                  </div>
                </div>
              </div>

              {/* Mobile Actions Overlay */}
              <div className="md:hidden flex gap-4 mt-6">
                <button onClick={() => openEdit(member)} className="px-6 py-2 rounded-xl bg-white/5 text-white border border-white/10 text-xs font-bold uppercase tracking-widest">Edit</button>
                <button onClick={() => handleDelete(member.id)} className="px-6 py-2 rounded-xl bg-red-500/10 text-red-500 border border-red-500/20 text-xs font-bold uppercase tracking-widest">Purge</button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Modal - Onboarding Form */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-md" 
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="glass-panel w-full max-w-2xl p-0 overflow-hidden relative z-10 border-white/10 shadow-2xl"
            >
              <div className="p-8 border-b border-white/5 flex items-center justify-between bg-gradient-to-r from-blue-500/10 to-transparent">
                <div>
                  <h2 className="admin-heading text-2xl font-black text-white italic uppercase tracking-tighter">{editingMember ? 'Update Record' : 'Onboard Architect'}</h2>
                  <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-1">Neural Personnel Indexing</p>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/5 rounded-full transition-colors text-slate-500 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-8 grid grid-cols-2 gap-x-6 gap-y-8">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 italic">Identity Fragment (Name)</label>
                  <input 
                    type="text" 
                    className="input-premium font-bold tracking-tight italic"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Salman Khan"
                    required
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 italic">Operational Core (Role)</label>
                  <input 
                    type="text" 
                    className="input-premium font-bold tracking-tight"
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    placeholder="e.g., Lead Systems Architect"
                    required
                  />
                </div>
                <ImageDropzone
                  label="Visual Signature (Profile Image)"
                  value={formData.image}
                  onChange={(url) => setFormData({...formData, image: url})}
                  className="col-span-2"
                />
                <div className="col-span-2">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 italic">Neural Profile (Bio)</label>
                  <textarea 
                    className="input-premium h-32 resize-none py-4 font-medium"
                    value={formData.about_text}
                    onChange={(e) => setFormData({...formData, about_text: e.target.value})}
                    placeholder="Describe the architect's specialization and core contributions..."
                    required
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 italic">Priority (Order)</label>
                  <input 
                    type="number" 
                    className="input-premium font-mono bg-white/5"
                    value={formData.display_order}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                  />
                </div>

                <div className="col-span-2 flex gap-4 pt-6 border-t border-white/5">
                  <button type="submit" className="btn-premium btn-premium-primary flex-1 py-4 text-base shadow-blue-500/20 italic font-black uppercase tracking-widest">
                    {editingMember ? 'Save Profile' : 'Authorize Entrance'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowModal(false)}
                    className="btn-premium btn-premium-outline px-10 border-white/10"
                  >
                    Abort
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminTeamPage;
